/**
 * Product vocabulary — the single source of truth for customer-facing naming.
 *
 * OmniCloud wraps OpenStack, but customers must never see OpenStack's vocabulary.
 * Every user-facing label should come from here (or from a component that uses it,
 * e.g. <StatusTag>). See docs/planning/OMNICLOUD_AWS_UX_PLAN.md (Phase 0/1).
 */

/** OpenStack concept -> the word customers see. */
export const TERMS = {
  flavor: "Instance size",
  instance: "Instance",
  keypair: "SSH key",
  floatingIp: "Public IP",
  securityGroup: "Firewall",
  image: "Image",
  volume: "Volume",
  project: "Project",
  loadBalancer: "Load balancer",
  stack: "Blueprint",
  cluster: "Kubernetes cluster",
  database: "Managed database",
} as const;

export type TermKey = keyof typeof TERMS;

/** Semantic tone for a resource status; mapped to a colour by <StatusTag>. */
export type StatusTone = "success" | "processing" | "error" | "warning" | "neutral";

export interface StatusMeta {
  /** Friendly, customer-facing label (e.g. "Running"). */
  label: string;
  tone: StatusTone;
}

/** Resource families that have their own status vocabulary. */
export type ResourceKind =
  | "instance"
  | "volume"
  | "image"
  | "loadbalancer"
  | "cluster"
  | "stack"
  | "database"
  | "network"
  | "dns"
  | "secret"
  | "alarm"
  | "operation"
  | "optimization"
  | "function"
  | "queue"
  | "topic"
  | "subscription"
  | "delivery"
  | "migration"
  | "vpn"
  | "acl"
  | "mirror"
  | "share"
  | "distribution"
  | "autoscaling"
  | "agent_gateway"
  | "agent"
  | "video"
  | "generic";

// Curated mappings for the statuses customers see most. Keys are upper-cased.
const INSTANCE_STATUS: Record<string, StatusMeta> = {
  ACTIVE: { label: "Running", tone: "success" },
  SHUTOFF: { label: "Stopped", tone: "neutral" },
  BUILD: { label: "Creating", tone: "processing" },
  BUILDING: { label: "Creating", tone: "processing" },
  REBOOT: { label: "Rebooting", tone: "processing" },
  HARD_REBOOT: { label: "Rebooting", tone: "processing" },
  PAUSED: { label: "Paused", tone: "warning" },
  SUSPENDED: { label: "Suspended", tone: "warning" },
  SHELVED: { label: "Archived", tone: "warning" },
  SHELVED_OFFLOADED: { label: "Archived", tone: "warning" },
  RESIZE: { label: "Resizing", tone: "processing" },
  VERIFY_RESIZE: { label: "Confirming resize", tone: "processing" },
  DELETING: { label: "Deleting", tone: "processing" },
  ERROR: { label: "Failed", tone: "error" },
  // Synthetic optimistic transition states (client-side, see compute/hooks.ts).
  POWERING_ON: { label: "Starting", tone: "processing" },
  POWERING_OFF: { label: "Stopping", tone: "processing" },
  REBOOTING: { label: "Rebooting", tone: "processing" },
  REBUILD: { label: "Rebuilding", tone: "processing" },
  PAUSING: { label: "Pausing", tone: "processing" },
  RESUMING: { label: "Resuming", tone: "processing" },
  SUSPENDING: { label: "Suspending", tone: "processing" },
  SHELVING: { label: "Archiving", tone: "processing" },
  UNSHELVING: { label: "Restoring", tone: "processing" },
  // From the cloud's task state (compute/status.ts): the instance runs on.
  IMAGE_SNAPSHOT: { label: "Creating image", tone: "processing" },
};

const VOLUME_STATUS: Record<string, StatusMeta> = {
  AVAILABLE: { label: "Available", tone: "success" },
  "IN-USE": { label: "In use", tone: "success" },
  CREATING: { label: "Creating", tone: "processing" },
  DELETING: { label: "Deleting", tone: "processing" },
  ATTACHING: { label: "Attaching", tone: "processing" },
  DETACHING: { label: "Detaching", tone: "processing" },
  ERROR: { label: "Failed", tone: "error" },
};

const IMAGE_STATUS: Record<string, StatusMeta> = {
  ACTIVE: { label: "Ready", tone: "success" },
  QUEUED: { label: "Queued", tone: "processing" },
  SAVING: { label: "Saving", tone: "processing" },
  UPLOADING: { label: "Uploading", tone: "processing" },
  IMPORTING: { label: "Importing", tone: "processing" },
  DEACTIVATED: { label: "Deactivated", tone: "warning" },
  DELETED: { label: "Deleted", tone: "neutral" },
  KILLED: { label: "Failed", tone: "error" },
};

const LB_STATUS: Record<string, StatusMeta> = {
  ACTIVE: { label: "Active", tone: "success" },
  ONLINE: { label: "Online", tone: "success" },
  OFFLINE: { label: "Offline", tone: "neutral" },
  DEGRADED: { label: "Degraded", tone: "warning" },
  PENDING_CREATE: { label: "Creating", tone: "processing" },
  PENDING_UPDATE: { label: "Updating", tone: "processing" },
  PENDING_DELETE: { label: "Deleting", tone: "processing" },
  NO_MONITOR: { label: "Not monitored", tone: "neutral" },
  ERROR: { label: "Failed", tone: "error" },
};

// Magnum reports Heat-style lifecycle statuses (e.g. CREATE_COMPLETE). The
// heuristic can't disambiguate these (CREATE_COMPLETE contains "creat"), so map
// them explicitly.
const CLUSTER_STATUS: Record<string, StatusMeta> = {
  CREATE_IN_PROGRESS: { label: "Creating", tone: "processing" },
  CREATE_COMPLETE: { label: "Ready", tone: "success" },
  CREATE_FAILED: { label: "Failed", tone: "error" },
  UPDATE_IN_PROGRESS: { label: "Updating", tone: "processing" },
  UPDATE_COMPLETE: { label: "Ready", tone: "success" },
  UPDATE_FAILED: { label: "Failed", tone: "error" },
  DELETE_IN_PROGRESS: { label: "Deleting", tone: "processing" },
  DELETE_FAILED: { label: "Failed", tone: "error" },
  ROLLBACK_IN_PROGRESS: { label: "Rolling back", tone: "warning" },
  ROLLBACK_COMPLETE: { label: "Rolled back", tone: "warning" },
};

// Heat stacks ("blueprints") share the cluster lifecycle vocabulary, plus a
// few states Magnum never reports.
const STACK_STATUS: Record<string, StatusMeta> = {
  ...CLUSTER_STATUS,
  DELETE_COMPLETE: { label: "Deleted", tone: "neutral" },
  SUSPEND_IN_PROGRESS: { label: "Suspending", tone: "processing" },
  SUSPEND_COMPLETE: { label: "Suspended", tone: "warning" },
  RESUME_IN_PROGRESS: { label: "Resuming", tone: "processing" },
  RESUME_COMPLETE: { label: "Ready", tone: "success" },
  CHECK_IN_PROGRESS: { label: "Checking", tone: "processing" },
  CHECK_COMPLETE: { label: "Ready", tone: "success" },
  SNAPSHOT_IN_PROGRESS: { label: "Snapshotting", tone: "processing" },
  SNAPSHOT_COMPLETE: { label: "Ready", tone: "success" },
};

// Trove managed-database lifecycle. A database "instance" should read like a
// compute instance — the list and detail page must both say "Running", never
// one "Running" and the other "Active".
const DATABASE_STATUS: Record<string, StatusMeta> = {
  ACTIVE: { label: "Running", tone: "success" },
  HEALTHY: { label: "Running", tone: "success" },
  BUILD: { label: "Creating", tone: "processing" },
  BUILDING: { label: "Creating", tone: "processing" },
  REBOOT: { label: "Rebooting", tone: "processing" },
  RESIZE: { label: "Resizing", tone: "processing" },
  UPGRADE: { label: "Upgrading", tone: "processing" },
  BACKUP: { label: "Backing up", tone: "processing" },
  SHUTDOWN: { label: "Stopped", tone: "neutral" },
  RESTART_REQUIRED: { label: "Restart required", tone: "warning" },
  PROMOTE: { label: "Promoting", tone: "processing" },
  DETACH: { label: "Detaching", tone: "processing" },
  // Trove's state while a new source is chosen for a replica set whose source
  // is gone (never a customer action; expose plan EX-3 naming).
  EJECT: { label: "Changing source", tone: "processing" },
  DELETING: { label: "Deleting", tone: "processing" },
  ERROR: { label: "Failed", tone: "error" },
  FAILED: { label: "Failed", tone: "error" },
  // Backup lifecycle (shares the family).
  NEW: { label: "Queued", tone: "processing" },
  SAVING: { label: "Saving", tone: "processing" },
  COMPLETED: { label: "Completed", tone: "success" },
  DELETE_FAILED: { label: "Delete failed", tone: "error" },
};

// Neutron networks / routers / ports / floating IPs. "DOWN" is a normal state
// for an unbound port or an unattached public IP — neutral, not an error.
const NETWORK_STATUS: Record<string, StatusMeta> = {
  ACTIVE: { label: "Active", tone: "success" },
  DOWN: { label: "Not attached", tone: "neutral" },
  BUILD: { label: "Creating", tone: "processing" },
  PENDING_CREATE: { label: "Creating", tone: "processing" },
  PENDING_UPDATE: { label: "Updating", tone: "processing" },
  PENDING_DELETE: { label: "Deleting", tone: "processing" },
  ERROR: { label: "Failed", tone: "error" },
};

// Designate zones / recordsets / PTRs. INACTIVE needs an explicit entry: the
// generic tone heuristic substring-matches "ACTIVE" and would colour it green.
const DNS_STATUS: Record<string, StatusMeta> = {
  ACTIVE: { label: "Active", tone: "success" },
  INACTIVE: { label: "Inactive", tone: "neutral" },
  PENDING: { label: "Pending", tone: "processing" },
  DELETED: { label: "Deleted", tone: "neutral" },
  ERROR: { label: "Failed", tone: "error" },
};

// Barbican secrets / containers / orders.
const SECRET_STATUS: Record<string, StatusMeta> = {
  ACTIVE: { label: "Active", tone: "success" },
  PENDING: { label: "Pending", tone: "processing" },
  EXPIRED: { label: "Expired", tone: "warning" },
  ERROR: { label: "Failed", tone: "error" },
};

// Alarm evaluation states (CloudWatch-style).
const ALARM_STATUS: Record<string, StatusMeta> = {
  OK: { label: "OK", tone: "success" },
  ALARM: { label: "Alarm", tone: "error" },
  INSUFFICIENT_DATA: { label: "Insufficient data", tone: "neutral" },
};

// Async-operation (Temporal workflow) lifecycle states. A running workflow is
// "processing", not "success" — so these need an explicit map rather than the
// generic heuristic (which would colour RUNNING green).
const OPERATION_STATUS: Record<string, StatusMeta> = {
  PENDING: { label: "Pending", tone: "processing" },
  RUNNING: { label: "In progress", tone: "processing" },
  COMPLETED: { label: "Completed", tone: "success" },
  FAILED: { label: "Failed", tone: "error" },
  CANCELED: { label: "Canceled", tone: "neutral" },
  TERMINATED: { label: "Terminated", tone: "error" },
  TIMED_OUT: { label: "Timed out", tone: "error" },
  UNKNOWN: { label: "Unknown", tone: "neutral" },
};

// Optimization audit / action-plan lifecycle states. RECOMMENDED is the
// "review me" resting state of a plan — neutral, not success; SUPERSEDED means
// a newer plan replaced it.
const OPTIMIZATION_STATUS: Record<string, StatusMeta> = {
  PENDING: { label: "Pending", tone: "processing" },
  ONGOING: { label: "In progress", tone: "processing" },
  SUCCEEDED: { label: "Succeeded", tone: "success" },
  FAILED: { label: "Failed", tone: "error" },
  CANCELLED: { label: "Cancelled", tone: "neutral" },
  CANCELLING: { label: "Cancelling", tone: "processing" },
  SUSPENDED: { label: "Suspended", tone: "warning" },
  RECOMMENDED: { label: "Ready to review", tone: "warning" },
  SUPERSEDED: { label: "Superseded", tone: "neutral" },
  DELETED: { label: "Deleted", tone: "neutral" },
  // Not a Watcher state: the Recommendations list shows it for an audit that
  // is still running and has not produced its plan yet.
  AUDIT_IN_PROGRESS: { label: "Audit in progress", tone: "processing" },
};

// Serverless function lifecycle (BUILDING is the pre-deploy source build).
const FUNCTION_STATUS: Record<string, StatusMeta> = {
  READY: { label: "Ready", tone: "success" },
  BUILDING: { label: "Building", tone: "processing" },
  DEPLOYING: { label: "Deploying", tone: "processing" },
  FAILED: { label: "Failed", tone: "error" },
  RUNNING: { label: "Running", tone: "processing" },
  PENDING: { label: "Pending", tone: "processing" },
  SUCCEEDED: { label: "Succeeded", tone: "success" },
};

// Message-queue lifecycle: non-active states are background sweeps in
// progress (purge/redrive/delete run as batched async operations).
const QUEUE_STATUS: Record<string, StatusMeta> = {
  ACTIVE: { label: "Active", tone: "success" },
  PURGING: { label: "Purging", tone: "processing" },
  REDRIVING: { label: "Moving messages", tone: "processing" },
  DELETING: { label: "Deleting", tone: "processing" },
};

const TOPIC_STATUS: Record<string, StatusMeta> = {
  ACTIVE: { label: "Active", tone: "success" },
  DELETING: { label: "Deleting", tone: "processing" },
};

// Subscription circuit state: "backing_off" is derived in the UI from
// consecutive_failures > 0; "disabled" is the auto/explicit circuit break.
const SUBSCRIPTION_STATUS: Record<string, StatusMeta> = {
  ENABLED: { label: "Healthy", tone: "success" },
  BACKING_OFF: { label: "Retrying", tone: "warning" },
  DISABLED: { label: "Disabled", tone: "error" },
};

// "retrying" is derived in the UI (pending with failed attempts) and reads
// as its subscription does.
const DELIVERY_STATUS: Record<string, StatusMeta> = {
  PENDING: { label: "Pending", tone: "processing" },
  RETRYING: { label: "Retrying", tone: "warning" },
  DELIVERED: { label: "Delivered", tone: "success" },
  FAILED: { label: "Failed", tone: "error" },
};

// Nova migration lifecycle (os-migrations). Live and cold moves report
// different words for the same phases, and both sets are here because both
// reach this table. "finished" is the one that matters: a cold move sits
// there, disk copied but not yet accepted, until the operator confirms it —
// so it reads as a warning (you must act), not a neutral in-progress.
const MIGRATION_STATUS: Record<string, StatusMeta> = {
  ACCEPTED: { label: "Starting", tone: "processing" },
  QUEUED: { label: "Queued", tone: "processing" },
  PREPARING: { label: "Preparing", tone: "processing" },
  RUNNING: { label: "Moving", tone: "processing" },
  MIGRATING: { label: "Moving", tone: "processing" },
  "POST-MIGRATING": { label: "Finishing", tone: "processing" },
  FINISHED: { label: "Awaiting confirmation", tone: "warning" },
  CONFIRMING: { label: "Awaiting confirmation", tone: "warning" },
  COMPLETED: { label: "Moved", tone: "success" },
  CONFIRMED: { label: "Moved", tone: "success" },
  REVERTING: { label: "Reverting", tone: "processing" },
  REVERTED: { label: "Reverted", tone: "neutral" },
  DONE: { label: "Moved", tone: "success" },
  CANCELLED: { label: "Cancelled", tone: "neutral" },
  FAILED: { label: "Failed", tone: "error" },
  ERROR: { label: "Failed", tone: "error" },
};

// Site-to-site VPN connections. DOWN here is a real problem (the tunnel is
// not up), unlike a DOWN port; INACTIVE is the customer having disabled it.
const VPN_STATUS: Record<string, StatusMeta> = {
  ACTIVE: { label: "Connected", tone: "success" },
  DOWN: { label: "Tunnel down", tone: "error" },
  INACTIVE: { label: "Disabled", tone: "neutral" },
  PENDING_CREATE: { label: "Connecting", tone: "processing" },
  PENDING_UPDATE: { label: "Updating", tone: "processing" },
  PENDING_DELETE: { label: "Deleting", tone: "processing" },
  ERROR: { label: "Failed", tone: "error" },
};

// Network ACLs (activation plan item 1). INACTIVE is Neutron's word for "no
// router attached yet" — nothing is wrong, the rules just guard nothing; DOWN
// means the enforcing router port is down.
const ACL_STATUS: Record<string, StatusMeta> = {
  ACTIVE: { label: "Active", tone: "success" },
  INACTIVE: { label: "Not attached", tone: "neutral" },
  DOWN: { label: "Down", tone: "error" },
  PENDING_CREATE: { label: "Creating", tone: "processing" },
  PENDING_UPDATE: { label: "Updating", tone: "processing" },
  PENDING_DELETE: { label: "Deleting", tone: "processing" },
  ERROR: { label: "Failed", tone: "error" },
};

// Traffic mirroring (activation plan 2, item 3): a mirror is Active once the
// copies flow to the monitor; Down means the monitor or a source port is not
// bound to a running server.
const MIRROR_STATUS: Record<string, StatusMeta> = {
  ACTIVE: { label: "Active", tone: "success" },
  DOWN: { label: "Down", tone: "error" },
  INACTIVE: { label: "Inactive", tone: "neutral" },
  PENDING_CREATE: { label: "Creating", tone: "processing" },
  PENDING_UPDATE: { label: "Updating", tone: "processing" },
  PENDING_DELETE: { label: "Deleting", tone: "processing" },
  ERROR: { label: "Failed", tone: "error" },
};

// File shares (platform expansion item 4) and their snapshots / access rules.
// Reverting is the file server restoring from a snapshot; the *_ERROR pairs
// say which step failed so the customer knows what to retry.
const SHARE_STATUS: Record<string, StatusMeta> = {
  AVAILABLE: { label: "Available", tone: "success" },
  CREATING: { label: "Creating", tone: "processing" },
  EXTENDING: { label: "Extending", tone: "processing" },
  REVERTING: { label: "Restoring", tone: "processing" },
  DELETING: { label: "Deleting", tone: "processing" },
  MANAGE_STARTING: { label: "Preparing", tone: "processing" },
  ERROR: { label: "Failed", tone: "error" },
  ERROR_DELETING: { label: "Delete failed", tone: "error" },
  // A failed resize leaves the share unmountable until support recovers it.
  EXTENDING_ERROR: { label: "Resize failed — contact support", tone: "error" },
  SHRINKING_ERROR: { label: "Resize failed — contact support", tone: "error" },
  // Access-rule states.
  QUEUED_TO_APPLY: { label: "Applying", tone: "processing" },
  APPLYING: { label: "Applying", tone: "processing" },
  ACTIVE: { label: "Active", tone: "success" },
};

// Content delivery (CONTENT_DELIVERY_PLAN.md CDN-4): a distribution's status is
// derived from what the delivery nodes report. "degraded" is the origin not
// answering (the distribution keeps serving what it has cached); "error" is a
// configuration that could not be applied, with the reason beside it.
// PENDING / COMPLETED are an invalidation's two states.
const DISTRIBUTION_STATUS: Record<string, StatusMeta> = {
  CREATING: { label: "Creating", tone: "processing" },
  UPDATING: { label: "Updating", tone: "processing" },
  ACTIVE: { label: "Active", tone: "success" },
  DEGRADED: { label: "Origin unreachable", tone: "warning" },
  ERROR: { label: "Failed", tone: "error" },
  DELETING: { label: "Deleting", tone: "processing" },
  PENDING: { label: "In progress", tone: "processing" },
  COMPLETED: { label: "Completed", tone: "success" },
  // A custom domain's states (CDN-3), then its certificate's.
  PENDING_DNS: { label: "Waiting for DNS record", tone: "processing" },
  PENDING_CERTIFICATE: { label: "Waiting for certificate", tone: "processing" },
  FAILED: { label: "Failed", tone: "error" },
  NONE: { label: "No certificate yet", tone: "neutral" },
  OK: { label: "Valid", tone: "success" },
  EXPIRING: { label: "Expires soon", tone: "warning" },
  EXPIRED: { label: "Expired", tone: "error" },
};

// Auto scaling groups (activation phase 2). The group's own lifecycle is the
// only status shown; member servers carry ordinary instance statuses.
const AUTOSCALING_STATUS: Record<string, StatusMeta> = {
  CREATING: { label: "Creating", tone: "processing" },
  ACTIVE: { label: "Active", tone: "success" },
  UPDATING: { label: "Updating", tone: "processing" },
  DELETING: { label: "Deleting", tone: "processing" },
  ERROR: { label: "Failed", tone: "error" },
};

// Agent gateways (AGENT_GATEWAY_PLAN.md AGW-5): "degraded" is the previous
// configuration still serving after a rejected change (warning, with the
// reason beside it); "unreachable" is a gateway that stopped reporting.
const AGENT_GATEWAY_STATUS: Record<string, StatusMeta> = {
  CREATING: { label: "Creating", tone: "processing" },
  ACTIVE: { label: "Active", tone: "success" },
  DEGRADED: { label: "Degraded", tone: "warning" },
  UNREACHABLE: { label: "Unreachable", tone: "error" },
  DELETING: { label: "Deleting", tone: "processing" },
  ERROR: { label: "Failed", tone: "error" },
  // an update in place or a size change is running (AGW-10); the reason says which
  MAINTENANCE: { label: "Updating", tone: "processing" },
};

// Agents hosted by a gateway: desired vs observed state folded into one word.
const AGENT_STATUS: Record<string, StatusMeta> = {
  RUNNING: { label: "Running", tone: "success" },
  STARTING: { label: "Starting", tone: "processing" },
  STOPPING: { label: "Stopping", tone: "processing" },
  STOPPED: { label: "Stopped", tone: "neutral" },
  PAUSING: { label: "Pausing", tone: "processing" },
  PAUSED: { label: "Paused", tone: "neutral" },
  RESUMING: { label: "Resuming", tone: "processing" },
  EXITED: { label: "Exited", tone: "warning" },
  BUILDING: { label: "Building", tone: "processing" },
  BUILD_FAILED: { label: "Build failed", tone: "error" },
};

// Video streaming (VIDEO_STREAMING_PLAN.md VID-7, naming Pass 9): an upload
// goes through processing, then the video is ready or failed ("errored" is the
// API's word; the reason beside it is a fixed sentence).
const VIDEO_STATUS: Record<string, StatusMeta> = {
  WAITING_FOR_UPLOAD: { label: "Waiting for upload", tone: "neutral" },
  PROCESSING: { label: "Processing", tone: "processing" },
  READY: { label: "Ready", tone: "success" },
  ERRORED: { label: "Failed", tone: "error" },
  DELETING: { label: "Deleting", tone: "processing" },
};

const KIND_MAPS: Record<ResourceKind, Record<string, StatusMeta>> = {
  instance: INSTANCE_STATUS,
  volume: VOLUME_STATUS,
  image: IMAGE_STATUS,
  loadbalancer: LB_STATUS,
  cluster: CLUSTER_STATUS,
  stack: STACK_STATUS,
  database: DATABASE_STATUS,
  network: NETWORK_STATUS,
  dns: DNS_STATUS,
  secret: SECRET_STATUS,
  alarm: ALARM_STATUS,
  operation: OPERATION_STATUS,
  optimization: OPTIMIZATION_STATUS,
  function: FUNCTION_STATUS,
  queue: QUEUE_STATUS,
  topic: TOPIC_STATUS,
  subscription: SUBSCRIPTION_STATUS,
  delivery: DELIVERY_STATUS,
  migration: MIGRATION_STATUS,
  vpn: VPN_STATUS,
  acl: ACL_STATUS,
  mirror: MIRROR_STATUS,
  share: SHARE_STATUS,
  distribution: DISTRIBUTION_STATUS,
  autoscaling: AUTOSCALING_STATUS,
  agent_gateway: AGENT_GATEWAY_STATUS,
  agent: AGENT_STATUS,
  video: VIDEO_STATUS,
  generic: {},
};

/** Title-case a raw token: "hard_reboot" / "in-use" -> "Hard reboot" / "In use". */
function titleCase(raw: string): string {
  const s = raw.replace(/[_-]+/g, " ").trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Heuristic tone for statuses we have not explicitly mapped. */
function guessTone(raw: string): StatusTone {
  const s = raw.toUpperCase();
  if (/ERROR|FAIL|KILLED|DEGRADED/.test(s)) return "error";
  if (/PENDING|BUILD|CREAT|DELET|UPDAT|SAV|ING\b|REBOOT|RESIZE|MIGRAT/.test(s)) return "processing";
  if (/ACTIVE|AVAILABLE|ONLINE|READY|RUNNING|IN.USE|UP\b/.test(s)) return "success";
  if (/PAUSED|SUSPEND|WARN/.test(s)) return "warning";
  return "neutral";
}

/**
 * Map a raw OpenStack status to a friendly, customer-facing label + tone.
 * Falls back to a title-cased label with a heuristic tone for unmapped values,
 * so it degrades gracefully across every resource type.
 */
export function humanizeStatus(
  raw?: string | null,
  kind: ResourceKind = "generic",
): StatusMeta {
  if (!raw) return { label: "Unknown", tone: "neutral" };
  const key = raw.toUpperCase();
  const mapped = KIND_MAPS[kind][key] ?? KIND_MAPS.generic[key];
  return mapped ?? { label: titleCase(raw), tone: guessTone(raw) };
}
